import { useEffect, useMemo, useState } from 'react';
import { CalendarRange, ChevronDown, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import { formatDayDate, formatDayTitle } from '@/lib/builder/components/day-labels';
import { ActivityTypeIcon } from '@/lib/builder/models/activity-type-icons';
import {
  fetchTemplatePreview,
  type TemplatePreview,
  type TemplatePreviewActivity,
} from '@/services/itinerary.service';

interface SaveAsTemplatePreviewProps {
  itineraryId: string;
  /** Il fetch parte solo a modale aperta: la modale globale resta montata da chiusa. */
  isOpen: boolean;
}

/** Sotto questa soglia i giorni si aprono da soli: l'elenco resta comunque corto. */
const AUTO_EXPAND_MAX_DAYS = 3;

/**
 * Anteprima della conversione date → giorni relativi.
 *
 * Mostra prima di tutto la durata del template, che è il numero con cui il template
 * verrà poi confrontato quando lo si collega a una richiesta di viaggio, e sotto la
 * mappatura giorno per giorno. È informativa: se la lettura fallisce si degrada a un
 * avviso e il salvataggio resta possibile.
 */
export const SaveAsTemplatePreview = ({ itineraryId, isOpen }: SaveAsTemplatePreviewProps) => {
  const { t, language } = useI18n();
  const locale = language || 'it-IT';

  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!isOpen || !itineraryId) return;

    let cancelled = false;
    setIsLoading(true);
    setHasFailed(false);
    setPreview(null);

    fetchTemplatePreview(itineraryId)
      .then((result) => {
        if (cancelled) return;
        setPreview(result);
        setExpandedDays(
          result.durationDays <= AUTO_EXPAND_MAX_DAYS
            ? new Set(result.days.map((day) => day.index))
            : new Set()
        );
      })
      .catch((err) => {
        if (cancelled) return;
        // L'anteprima è un di più: si annota e si va avanti, il salvataggio non dipende da qui.
        console.error('Error loading template preview:', err);
        setHasFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [itineraryId, isOpen]);

  /** "Giorno N": l'etichetta la costruisce il formatter condiviso, non questa modale. */
  const dayLabel = useMemo(
    () => (index: number) =>
      formatDayTitle(
        { date: null, index },
        { locale, fallback: (number) => t('builder.dayNumber', { number }) }
      ),
    [locale, t]
  );

  const toggleDay = (index: number) => {
    setExpandedDays((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const activitySpanLabel = (activity: TemplatePreviewActivity): string | null =>
    activity.endDayIndex > activity.startDayIndex
      ? t('itinerary.templatePreview.dayRange', {
          from: dayLabel(activity.startDayIndex),
          to: dayLabel(activity.endDayIndex),
        })
      : null;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 p-4 space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-48" />
        <div className="space-y-2 pt-1">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  if (hasFailed || !preview) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
        <span>{t('itinerary.templatePreview.loadError')}</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      {/* Intestazione: la durata è il dato che definisce il template, sta per prima. */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-sm font-semibold px-3 py-1">
            <CalendarRange className="h-4 w-4 mr-1.5" />
            {t('itinerary.templatePreview.daysCount', { count: preview.durationDays })}
          </Badge>
          <span className="text-sm text-gray-500">
            {t('itinerary.templatePreview.activitiesCount', { count: preview.activitiesCount })}
          </span>
        </div>

        {preview.startDate && preview.endDate ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>
              {t('itinerary.templatePreview.dateToDay', {
                date: formatDayDate(preview.startDate, locale),
                day: dayLabel(1),
              })}
            </span>
            <span>
              {t('itinerary.templatePreview.dateToDay', {
                date: formatDayDate(preview.endDate, locale),
                day: dayLabel(preview.durationDays),
              })}
            </span>
          </div>
        ) : (
          <p className="text-xs text-gray-500">{t('itinerary.templatePreview.noSourceDates')}</p>
        )}
      </div>

      {/* Un itinerario lungo non deve far crescere la modale: la lista scrolla. */}
      <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
        {preview.days.map((day) => {
          const isExpanded = expandedDays.has(day.index);

          return (
            <Collapsible key={day.index} open={isExpanded} onOpenChange={() => toggleDay(day.index)}>
              <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-gray-50">
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-gray-400 transition-transform',
                    isExpanded && 'rotate-180'
                  )}
                />
                <span className="text-sm font-medium text-gray-900">{dayLabel(day.index)}</span>
                {day.date && (
                  <span className="text-xs text-gray-400">{formatDayDate(day.date, locale)}</span>
                )}
                <span className="ml-auto text-xs text-gray-500">
                  {day.activities.length > 0
                    ? t('itinerary.templatePreview.activitiesCount', {
                        count: day.activities.length,
                      })
                    : t('itinerary.templatePreview.emptyDay')}
                </span>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <ul className="pb-2 pl-10 pr-4 space-y-1">
                  {day.activities.map((activity) => {
                    const span = activitySpanLabel(activity);
                    return (
                      <li key={activity.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="text-gray-400 shrink-0">
                          <ActivityTypeIcon type={activity.type} />
                        </span>
                        <span className="truncate">{activity.title}</span>
                        {span && (
                          <Badge variant="secondary" className="shrink-0 text-[11px] font-normal">
                            {span}
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                  {day.activities.length === 0 && (
                    <li className="text-sm text-gray-400">
                      {t('itinerary.templatePreview.emptyDay')}
                    </li>
                  )}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};
